// One allowlist for every piece of user HTML we render: alerts, responses,
// stories and profile bios. Most of it is CKEditor output (see ckeditor/src),
// so the tags below are what that build emits, and nothing else gets through.
import sanitizeHtml from 'sanitize-html';

// Newspage's own hosts, plus any subdomain of them (app., cdn., pay.).
// newspage.dev is the staging HOST from next.config.js.
const OWN_DOMAINS = ['newspage.media', 'newspage.news', 'newspage.dev'];

export const isOwnHost = (hostname = '') => {
    const host = hostname.toLowerCase().replace(/\.$/, '');
    return OWN_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`));
};

// Relative links and links to our own hosts are ours. `//host` is protocol
// relative and counts as external. mailto: is not a web page, so it is left
// alone rather than being opened in a new tab.
export const isExternalHref = (href = '') => {
    const value = href.trim();
    if (/^mailto:/i.test(value)) return false;
    if (!/^(https?:)?\/\//i.test(value)) return false;
    try {
        return !isOwnHost(
            new URL(value, 'https://app.newspage.media').hostname
        );
    } catch {
        return true;
    }
};

// CKEditor's media embed (previewsInData) wraps the iframe in a div whose
// inline style holds the aspect ratio. Only these layout values are kept.
const LENGTH = [/^-?\d+(\.\d+)?(%|px)?$/];

const OPTIONS = {
    allowedTags: [
        'p',
        'br',
        'strong',
        'b',
        'em',
        'i',
        'u',
        's',
        'ins',
        'del',
        'sub',
        'sup',
        'span',
        'ul',
        'ol',
        'li',
        'h2',
        'h3',
        'h4',
        'a',
        'blockquote',
        'figure',
        'figcaption',
        'img',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'div',
        'iframe',
    ],
    allowedAttributes: {
        a: ['href', 'target', 'rel'],
        img: ['src', 'alt', 'width', 'height'],
        th: ['colspan', 'rowspan'],
        td: ['colspan', 'rowspan'],
        div: ['data-oembed-url', 'style'],
        iframe: [
            'src',
            'width',
            'height',
            'style',
            'allow',
            'allowfullscreen',
            'frameborder',
        ],
        '*': ['class'],
    },
    // Image style and table/media wrappers from the editor; no free-form classes.
    allowedClasses: {
        '*': ['image', 'image-inline', 'image-style-*', 'table', 'media'],
    },
    allowedStyles: {
        div: {
            position: [/^relative$/],
            height: LENGTH,
            'padding-bottom': LENGTH,
        },
        iframe: {
            position: [/^absolute$/],
            width: LENGTH,
            height: LENGTH,
            top: LENGTH,
            left: LENGTH,
        },
    },
    allowedSchemes: ['http', 'https', 'mailto'],
    allowedSchemesByTag: {
        img: ['http', 'https'],
        iframe: ['https'],
    },
    // The providers CKEditor renders a preview iframe for.
    allowedIframeHostnames: [
        'www.youtube.com',
        'www.youtube-nocookie.com',
        'player.vimeo.com',
        'www.dailymotion.com',
        'open.spotify.com',
    ],
    allowIframeRelativeUrls: false,
    transformTags: {
        a: (tagName, attribs) => {
            if (!isExternalHref(attribs.href)) {
                return { tagName, attribs };
            }
            return {
                tagName,
                attribs: {
                    ...attribs,
                    rel: 'nofollow noopener',
                    target: '_blank',
                },
            };
        },
    },
};

export default function safeHtml(html) {
    if (!html) return '';
    return sanitizeHtml(String(html), OPTIONS);
}

// Plain text going into an HTML string, e.g. a title wrapped in <h1>.
export const escapeHtml = (text = '') =>
    String(text ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

// JSON-LD body for <script type="application/ld+json">. A "</script>" inside
// any string value would otherwise close the tag and run what follows.
export const jsonLd = (obj) => JSON.stringify(obj).replace(/</g, '\\u003c');
